'use client';
import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const STORAGE_KEY = 'vgt.activeBranchId';
const EVENT_KEY = 'vgt:active-branch-changed';

/** FE-20 (audit 07-frontend.md): storage bị chặn (Safari riêng tư nghiêm
 * ngặt, chính sách doanh nghiệp) khiến `localStorage` NÉM lỗi thay vì trả
 * null/no-op — không try/catch thì hook này ném lỗi ngay lúc render. */
function readActiveBranchId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function useActiveBranch() {
  // Lazy init đọc thẳng localStorage — tránh 1 tick đầu activeBranchId = null
  // làm hasPermission ẩn nhầm menu gắn chi nhánh (flash). An toàn hydration vì
  // mọi UI phụ thuộc branch đều gate sau khi user load xong (client fetch).
  const [activeBranchId, setActiveBranchId] = useState<string | null>(readActiveBranchId);
  const queryClient = useQueryClient();

  useEffect(() => {
    const onChange = (e: Event) =>
      setActiveBranchId((e as CustomEvent<{ branchId?: string }>).detail?.branchId || null);
    window.addEventListener(EVENT_KEY, onChange);
    return () => window.removeEventListener(EVENT_KEY, onChange);
  }, []);

  const switchBranch = useCallback(
    (id: string | 'ALL') => {
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // storage bị chặn — vẫn đổi state trong phiên hiện tại, chỉ không
        // nhớ được qua lần tải trang sau.
      }
      setActiveBranchId(id);
      window.dispatchEvent(
        new CustomEvent(EVENT_KEY, { detail: { branchId: id } }),
      );
      // Header X-Active-Branch-Id gắn vào mọi request nhưng query key không chứa
      // branchId — phải invalidate toàn bộ cache, nếu không bảng dữ liệu vẫn hiển
      // thị chi nhánh cũ cho tới khi hết staleTime.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  return {
    activeBranchId,
    isAll: activeBranchId === 'ALL',
    switchBranch,
  };
}
